import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function AdminLegal() {
  const [docs, setDocs] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [versions, setVersions] = useState([]);
  const [openVer, setOpenVer] = useState(null);

  const load = () => {
    api.get("/admin/legal").then((r) => {
      setDocs(r.data.items || []);
      setChecklist(r.data.kontrol_listesi || []);
    });
    api.get("/admin/legal/versions").then((r) => setVersions(r.data.items || []));
  };
  useEffect(() => { load(); }, []);

  const patch = (slug, k, v) => setDocs((ds) => ds.map((d) => (d.slug === slug ? { ...d, [k]: v } : d)));

  const save = async (d) => {
    await api.put(`/admin/legal/${d.slug}/draft`, { taslak_icerik: d.taslak_icerik || "", taslak_surum: d.taslak_surum || "" });
    toast.success("Taslak kaydedildi (yayımlanmadı)");
    load();
  };
  const publish = async (d) => {
    if (!window.confirm(`${d.baslik} taslağı sürüm ${d.taslak_surum} olarak yayımlansın mı?`)) return;
    try {
      await api.put(`/admin/legal/${d.slug}/draft`, { taslak_icerik: d.taslak_icerik || "", taslak_surum: d.taslak_surum || "" });
      await api.post(`/admin/legal/${d.slug}/publish`);
      toast.success("Yayımlandı");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-4xl" data-testid="admin-legal-page">
      <h1 className="font-head text-2xl font-bold text-slate-900 mb-1">Yasal Metinler</h1>
      <p className="text-sm text-slate-500 mb-6">
        Taslaklar sitede görünmez. Firmanızın gerçek uygulamalarıyla doğruladıktan ve [doldurulacak] alanlarını tamamladıktan sonra yayımlayın.
      </p>

      <section className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6" data-testid="legal-checklist">
        <h2 className="font-head font-semibold text-amber-900 mb-2">Eksik İşletme Bilgileri — Kontrol Listesi</h2>
        <ul className="list-disc ml-5 text-sm text-amber-900 space-y-1">
          {checklist.map((c) => <li key={c}>{c}</li>)}
        </ul>
      </section>

      {docs.map((d) => (
        <section key={d.slug} className="bg-white border rounded-xl p-5 mb-6" data-testid={`legal-editor-${d.slug}`}>
          <h2 className="font-head font-semibold text-slate-900">{d.baslik}</h2>
          <p className="text-xs text-slate-500 mt-1">
            Yayındaki sürüm: <b>{d.surum || "—"}</b> ({d.guncelleme_tarihi || "—"}) · Taslak tarihi: {d.taslak_tarihi || "—"}
          </p>
          <div className="grid sm:grid-cols-[160px_1fr] gap-3 mt-4 items-end">
            <div>
              <Label>Taslak sürüm no</Label>
              <Input value={d.taslak_surum || ""} onChange={(e) => patch(d.slug, "taslak_surum", e.target.value)}
                className="mt-1.5" data-testid={`legal-version-${d.slug}`} />
            </div>
            <p className="text-xs text-slate-400">Başlık için satır başına "## ", madde için "- " yazın.</p>
          </div>
          <Textarea value={d.taslak_icerik || ""} onChange={(e) => patch(d.slug, "taslak_icerik", e.target.value)}
            className="mt-3 min-h-[360px] text-sm font-mono" data-testid={`legal-draft-${d.slug}`} />
          <div className="flex gap-2 mt-3">
            <Button variant="outline" onClick={() => save(d)} data-testid={`legal-save-${d.slug}`}>Taslağı Kaydet</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => publish(d)} data-testid={`legal-publish-${d.slug}`}>Yayımla</Button>
          </div>
          {versions.filter((v) => v.slug === d.slug).length > 0 && (
            <div className="mt-4 border-t pt-3" data-testid={`legal-history-${d.slug}`}>
              <h3 className="text-xs font-semibold text-slate-600 mb-2">Yayımlanmış önceki sürümler</h3>
              {versions.filter((v) => v.slug === d.slug).map((v) => (
                <div key={`${v.slug}-${v.surum}`} className="text-xs mb-2">
                  <button onClick={() => setOpenVer(openVer === `${v.slug}-${v.surum}` ? null : `${v.slug}-${v.surum}`)}
                    data-testid={`legal-version-open-${v.slug}-${v.surum}`}
                    className="text-teal-700 hover:underline font-medium">
                    Sürüm {v.surum} · {(v.tarih || "").slice(0, 10)} · {v.yayimlayan}
                  </button>
                  {openVer === `${v.slug}-${v.surum}` && (
                    <pre className="mt-2 bg-slate-50 border rounded p-3 whitespace-pre-wrap font-sans text-slate-600 max-h-64 overflow-y-auto"
                      data-testid={`legal-version-body-${v.slug}-${v.surum}`}>{v.icerik}</pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
